import Firebase from './Firebase.js'

/**
 * Firebase Helper untuk menghindari CORS di localhost
 * Menggunakan Firebase SDK dengan konfigurasi yang optimal
 */
class FirebaseHelper {
  constructor() {
    this.db = null
    this.auth = null
    this.isInitialized = false
    this.connectionStatus = 'disconnected'
  }

  /**
   * Initialize Firebase dengan error handling yang lebih baik
   */
  async initialize() {
    try {
      console.log('🔧 Initializing Firebase Helper...')
      
      const { db, auth, setupFirebaseEmulator, authenticateAnonymously, checkAuthStatus } = Firebase
      
      if (!db) {
        throw new Error('Firebase db is not available')
      }
      
      this.db = db
      this.auth = auth
      this.isInitialized = true
      
      // Setup emulator jika di development
      if (process.env.NODE_ENV === 'development') {
        try {
          setupFirebaseEmulator()
          console.log('✅ Firebase Emulator connected on port 8081')
        } catch (emulatorError) {
          console.log('⚠️ Firebase Emulator not available:', emulatorError.message)
        }
      }
      
      // Check authentication
      console.log('🔐 Checking authentication status...')
      const authStatus = checkAuthStatus()
      if (!authStatus.isAuthenticated) {
        console.log('🔐 User not authenticated, attempting anonymous auth...')
        const authResult = await authenticateAnonymously()
        if (!authResult.success) {
          throw new Error(`Authentication failed: ${authResult.error}`)
        }
        console.log('✅ Authentication successful:', authResult.user.uid)
      } else {
        console.log('✅ User already authenticated:', authStatus.user.uid)
      }
      
      this.connectionStatus = 'connected'
      console.log('✅ Firebase Helper initialized successfully')
      
      return { success: true, message: 'Firebase Helper initialized' }
    } catch (error) {
      console.error('❌ Firebase Helper initialization failed:', error)
      this.connectionStatus = 'error'
      return { success: false, error: error.message }
    }
  }

  /**
   * Get data dari Firestore dengan retry dan timeout
   */
  async getData(collectionName, options = {}) {
    if (!this.isInitialized) {
      await this.initialize()
    }

    try {
      console.log(`📂 Getting data from collection: ${collectionName}`)
      
      // Ensure authentication
      const { authenticateAnonymously, checkAuthStatus } = Firebase
      const authStatus = checkAuthStatus()
      if (!authStatus.isAuthenticated) {
        console.log('🔐 User not authenticated, attempting anonymous auth...')
        const authResult = await authenticateAnonymously()
        if (!authResult.success) {
          throw new Error(`Authentication failed: ${authResult.error}`)
        }
        console.log('✅ Authentication successful:', authResult.user.uid)
      }
      
      const { collection, getDocs, query, where, orderBy, limit } = await import('firebase/firestore')
      
      let ref = collection(this.db, collectionName)
      
      // Apply query options
      if (options.where) {
        ref = query(ref, where(options.where.field, options.where.operator, options.where.value))
      }
      
      if (options.orderBy) {
        ref = query(ref, orderBy(options.orderBy.field, options.orderBy.direction || 'asc'))
      }
      
      if (options.limit) {
        ref = query(ref, limit(options.limit))
      }
      
      // Add timeout to prevent hanging
      const timeout = options.timeout || 10000
      const queryPromise = getDocs(ref)
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error(`Query timeout after ${timeout}ms`)), timeout)
      )
      
      const snapshot = await Promise.race([queryPromise, timeoutPromise])
      
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      
      console.log(`✅ Retrieved ${data.length} documents from ${collectionName}`)
      
      return {
        success: true,
        data,
        size: snapshot.size,
        empty: snapshot.empty
      }
    } catch (error) {
      console.error(`❌ Error getting data from ${collectionName}:`, error)
      
      // Handle specific Firebase errors
      if (error.code === 'permission-denied') {
        return { success: false, error: 'Permission denied - check Firestore security rules' }
      } else if (error.code === 'unavailable') {
        return { success: false, error: 'Firebase service unavailable - check network connection' }
      } else if (error.message.includes('timeout')) {
        return { success: false, error: 'Query timeout - network or service issue' }
      } else {
        return { success: false, error: error.message }
      }
    }
  }

  /**
   * Real-time listener dengan error handling
   */
  setupRealtimeListener(collectionName, callback, errorCallback) {
    if (!this.isInitialized) {
      this.initialize().then(() => {
        this.setupRealtimeListener(collectionName, callback, errorCallback)
      })
      return null
    }

    try {
      console.log(`👂 Setting up real-time listener for: ${collectionName}`)
      
      const { collection, onSnapshot } = require('firebase/firestore')
      const ref = collection(this.db, collectionName)
      
      return onSnapshot(ref, 
        (snapshot) => {
          const data = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
          console.log(`📡 Real-time update: ${data.length} documents`)
          callback(data)
        },
        (error) => {
          console.error(`❌ Real-time listener error for ${collectionName}:`, error)
          if (errorCallback) errorCallback(error)
        }
      )
    } catch (error) {
      console.error(`❌ Failed to setup real-time listener for ${collectionName}:`, error)
      if (errorCallback) errorCallback(error)
      return null
    }
  }

  /**
   * Test koneksi Firebase
   */
  async testConnection() {
    try {
      console.log('🧪 Testing Firebase connection...')
      
      const result = await this.getData('tracking_driver', { limit: 1 })
      
      if (result.success) {
        console.log('✅ Firebase connection test successful')
        return { success: true, message: 'Firebase connection OK' }
      } else {
        console.log('❌ Firebase connection test failed:', result.error)
        return { success: false, error: result.error }
      }
    } catch (error) {
      console.error('❌ Firebase connection test error:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Get connection status
   */
  getConnectionStatus() {
    return {
      isInitialized: this.isInitialized,
      connectionStatus: this.connectionStatus,
      db: !!this.db,
      auth: !!this.auth
    }
  }
}

// Export singleton instance
const firebaseHelper = new FirebaseHelper()
export default firebaseHelper 